import { entryKeys, merchantKeys, type Merchant } from '@taler/api-client'
import { systemClock, todayUtc } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { computeMerchantFrecency } from '../internal/merchant-targets.ts'
import type { Frecency } from '../internal/targets.ts'

/** Same sample the account/contact frecency composable uses - wide enough for a real
 * ranking, within the mock's own page-size ceiling. */
const FRECENCY_SAMPLE_SIZE = 100
const MERCHANT_LIST_LIMIT = 100

export interface UseEntryMerchantsOptions {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
  /** A typed query to search the server's full directory with - see `useMerchants`' own
   * `search`. Omitted or empty: the top `MERCHANT_LIST_LIMIT` by name, same as before. */
  readonly search?: () => string
}

export interface UseEntryMerchants {
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly merchants: ComputedRef<readonly Merchant[]>
  readonly frecency: ComputedRef<ReadonlyMap<string, Frecency>>
}

export function useEntryMerchants(options: UseEntryMerchantsOptions): UseEntryMerchants {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const search = computed(() => options.search?.() ?? '')

  const merchantsQuery = useQuery({
    queryKey: computed(() =>
      merchantKeys.list(options.ledgerId() ?? '', {
        limit: MERCHANT_LIST_LIMIT,
        ...(search.value === '' ? {} : { search: search.value }),
      }),
    ),
    queryFn: ({ signal }) =>
      api.merchants.list(
        options.ledgerId()!,
        { limit: MERCHANT_LIST_LIMIT, ...(search.value === '' ? {} : { search: search.value }) },
        signal,
      ),
    enabled: active,
    retry: false,
  })

  // Shared with `useEntryTargets`' own recent-entries query - same queryKey, one request.
  const recentQuery = useQuery({
    queryKey: computed(() => entryKeys.list(options.ledgerId() ?? '', { limit: FRECENCY_SAMPLE_SIZE })),
    queryFn: ({ signal }) => api.entries.list(options.ledgerId()!, { limit: FRECENCY_SAMPLE_SIZE }, signal),
    enabled: active,
    retry: false,
  })

  return {
    isLoading: computed(
      () => active.value && (merchantsQuery.isPending.value || recentQuery.isPending.value),
    ),
    error: computed(() => merchantsQuery.error.value ?? recentQuery.error.value),
    merchants: computed(() => merchantsQuery.data.value?.items ?? []),
    frecency: computed(() =>
      computeMerchantFrecency(recentQuery.data.value?.items ?? [], todayUtc(systemClock)),
    ),
  }
}
